import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { scheduleApi } from '../api';
import {
  SCHEDULE_CATEGORIES, SCHEDULE_CATEGORY_COLORS,
  SCHEDULE_STATUSES, SCHEDULE_STATUS_COLORS,
  EVENT_TYPES, EVENT_TYPE_COLORS,
  CONSULTATION_TYPES, CONSULTATION_TYPE_COLORS,
} from '../constants';

type SectionKey = 'event' | 'consultation';

const SECTION_CONFIG: Record<SectionKey, { label: string; apiSections: string[]; color: string; icon: string }> = {
  event: { label: '행사일정', apiSections: ['EVENT'], color: 'bg-pink-500', icon: 'ri-calendar-event-line' },
  consultation: { label: '상담일정', apiSections: ['CONSULTATION', 'TASTING'], color: 'bg-green-500', icon: 'ri-discuss-line' },
};

const EVENT_BADGE_COLORS: Record<string, { bg: string; dot: string; label: string }> = {
  '예식': { bg: 'bg-pink-100 text-pink-700', dot: 'bg-pink-500', label: '예식' },
  '기업': { bg: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', label: '기업' },
  '돌': { bg: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', label: '돌' },
  '가족': { bg: 'bg-green-100 text-green-700', dot: 'bg-green-500', label: '가족' },
  '대관': { bg: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500', label: '대관' },
};

const CONSULT_BADGE_COLORS: Record<string, { bg: string; label: string }> = {
  FIRST_VISIT: { bg: 'bg-blue-100 text-blue-700 border border-blue-300', label: '초방' },
  REVISIT: { bg: 'bg-teal-100 text-teal-700 border border-teal-300', label: '재방' },
  TASTING: { bg: 'bg-orange-100 text-orange-700 border border-orange-300', label: '시식' },
};

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const formatTime = (dt?: string) => {
  if (!dt) return '';
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

export default function WeddingHallSchedules() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'daily' | 'monthly'>('daily');

  // ===== 일간 일정 state =====
  const [dailyDate, setDailyDate] = useState(new Date());
  const [sections, setSections] = useState<Record<SectionKey, any[]>>({ event: [], consultation: [] });
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({ event: false, consultation: false });
  const [isDailyLoading, setIsDailyLoading] = useState(false);

  // ===== 월간 일정 state =====
  const [monthDate, setMonthDate] = useState(new Date());
  const [monthSchedules, setMonthSchedules] = useState<any[]>([]);
  const [isMonthLoading, setIsMonthLoading] = useState(false);

  const monthYear = monthDate.getFullYear();
  const monthMonth = monthDate.getMonth();

  // 일간 날짜 라벨
  const dailyDateStr = toDateStr(dailyDate);
  const dailyLabel = dailyDate.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });

  // 일간 데이터 fetch
  const fetchDailySchedules = useCallback(async () => {
    setIsDailyLoading(true);
    try {
      const dateStr = toDateStr(dailyDate);
      const data = await scheduleApi.list({
        viewMode: 'DAILY', dateFrom: dateStr, dateTo: dateStr, size: 999,
      });
      const sortByStart = (a: any, b: any) => new Date(a.startDt).getTime() - new Date(b.startDt).getTime();
      const all = [...(data.event || []), ...(data.consultation || []), ...(data.items || [])];
      setSections({
        event: all.filter((s: any) => s.section === 'EVENT' || s.scheduleCategory === 'EVENT').sort(sortByStart),
        consultation: all.filter((s: any) =>
          s.section === 'CONSULTATION' || s.section === 'TASTING' ||
          s.scheduleCategory === 'CONSULTATION' || s.scheduleCategory === 'TASTING'
        ).sort(sortByStart),
      });
    } catch (err) {
      console.error('일간 일정 조회 실패:', err);
    } finally {
      setIsDailyLoading(false);
    }
  }, [dailyDate]);

  // 월간 데이터 fetch
  const fetchMonthSchedules = useCallback(async () => {
    setIsMonthLoading(true);
    try {
      const lastDay = new Date(monthYear, monthMonth + 1, 0);
      const dateFrom = `${monthYear}-${String(monthMonth + 1).padStart(2, '0')}-01`;
      const dateTo = `${monthYear}-${String(monthMonth + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;
      const data = await scheduleApi.list({ dateFrom, dateTo, size: 999 });
      setMonthSchedules(data.items || data || []);
    } catch (err) {
      console.error('월간 일정 조회 실패:', err);
    } finally {
      setIsMonthLoading(false);
    }
  }, [monthYear, monthMonth]);

  useEffect(() => {
    if (activeTab === 'daily') fetchDailySchedules();
  }, [activeTab, fetchDailySchedules]);

  useEffect(() => {
    if (activeTab === 'monthly') fetchMonthSchedules();
  }, [activeTab, fetchMonthSchedules]);

  // 날짜 이동
  const prevDay = () => {
    const d = new Date(dailyDate);
    d.setDate(d.getDate() - 1);
    setDailyDate(d);
  };
  const nextDay = () => {
    const d = new Date(dailyDate);
    d.setDate(d.getDate() + 1);
    setDailyDate(d);
  };
  const prevMonth = () => setMonthDate(new Date(monthYear, monthMonth - 1, 1));
  const nextMonth = () => setMonthDate(new Date(monthYear, monthMonth + 1, 1));

  const toggleSection = (key: SectionKey) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const totalDailyCount = sections.event.length + sections.consultation.length;

  // 상태 체크박스 토글
  const handleStatusToggle = async (scheduleIdx: number, currentStatus: string) => {
    const newStatus = currentStatus === '완료' ? '예정' : '완료';
    try {
      await scheduleApi.update(scheduleIdx, { status: newStatus });
      fetchDailySchedules();
    } catch (err: any) {
      alert(`상태 변경 실패: ${err.message}`);
    }
  };

  const getEventTypeLabel = (value: string) => EVENT_TYPES.find(e => e.value === value)?.label || value;
  const getConsultTypeLabel = (value: string) => {
    if (value === 'TASTING') return '시식';
    return CONSULTATION_TYPES.find(c => c.value === value)?.label || value;
  };

  // ===== 월간 캘린더 헬퍼 =====
  const generateCalendar = () => {
    const firstDay = new Date(monthYear, monthMonth, 1);
    const lastDay = new Date(monthYear, monthMonth + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const weeks: (number | null)[][] = [];
    let week: (number | null)[] = [];
    for (let i = 0; i < startDay; i++) week.push(null);
    for (let d = 1; d <= totalDays; d++) {
      week.push(d);
      if (week.length === 7) { weeks.push(week); week = []; }
    }
    if (week.length > 0) {
      while (week.length < 7) week.push(null);
      weeks.push(week);
    }
    return weeks;
  };

  const getSchedulesForDay = (day: number) => {
    const dateStr = `${monthYear}-${String(monthMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return monthSchedules.filter((s: any) => s.startDt?.startsWith(dateStr));
  };

  const weeks = generateCalendar();
  const todayStr = toDateStr(new Date());

  // ===== 행사일정(EVENT) 테이블 =====
  const renderEventTable = (items: any[]) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left">
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">시간</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">예식홀</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-20 text-center">행사유형</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-32">고객명</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">메뉴</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-20 text-center">보증인원</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">업체</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-16 text-center">상태</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.map((s: any) => {
          const eventColor = EVENT_TYPE_COLORS[s.eventType] || 'bg-gray-100 text-gray-600';
          return (
            <tr key={s.weddingHallScheduleIdx || s.scheduleIdx || s.id} className="hover:bg-gray-50">
              <td className="px-3 py-2.5 text-gray-700 font-medium whitespace-nowrap">{formatTime(s.startDt)}</td>
              <td className="px-3 py-2.5 text-gray-800 font-medium text-sm">{s.venueName || s.venue || '-'}</td>
              <td className="px-3 py-2.5 text-center">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${eventColor}`}>
                  {getEventTypeLabel(s.eventType)}
                </span>
              </td>
              <td className="px-3 py-2.5">
                <div className="leading-tight">
                  {s.groomName && <span className="text-gray-500 text-xs"><i className="ri-men-line text-blue-400 mr-0.5"></i>{s.groomName}</span>}
                  {s.groomName && s.brideName && <span className="text-gray-400 mx-1">/</span>}
                  {s.brideName && <span className="text-gray-800 text-xs font-medium"><i className="ri-women-line text-pink-400 mr-0.5"></i>{s.brideName}</span>}
                  {!s.groomName && !s.brideName && <span className="text-gray-400">{s.customerName || '-'}</span>}
                </div>
              </td>
              <td className="px-3 py-2.5 text-gray-600 text-xs">{s.menuName || s.menu || '-'}</td>
              <td className="px-3 py-2.5 text-center">
                {s.guaranteeCount ? (
                  <span className="text-xs font-medium text-gray-700">{s.guaranteeCount.toLocaleString()}명</span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-3 py-2.5 text-gray-600 text-xs">{s.partnerVendorName || '-'}</td>
              <td className="px-3 py-2.5 text-center">
                <input type="checkbox" checked={s.status === '완료'}
                  onChange={() => handleStatusToggle(s.weddingHallScheduleIdx || s.scheduleIdx || s.id, s.status)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 cursor-pointer" />
              </td>
            </tr>
          );
        })}
        {items.length === 0 && (
          <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-400">등록된 행사일정이 없습니다.</td></tr>
        )}
      </tbody>
    </table>
  );

  // ===== 상담일정(CONSULTATION+TASTING) 테이블 =====
  const renderConsultationTable = (items: any[]) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left">
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">시간</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-20 text-center">상담유형</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-28">고객명</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-28">연락처</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">담당자</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 w-16 text-center">상태</th>
          <th className="px-3 py-2.5 text-xs font-semibold text-gray-500">메모</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.map((s: any) => {
          const consultType = s.scheduleCategory === 'TASTING' ? 'TASTING' : s.consultationType;
          const badge = CONSULT_BADGE_COLORS[consultType] || { bg: 'bg-gray-100 text-gray-600', label: getConsultTypeLabel(consultType) };
          return (
            <tr key={s.weddingHallScheduleIdx || s.scheduleIdx || s.id} className="hover:bg-gray-50">
              <td className="px-3 py-2.5 text-gray-700 font-medium whitespace-nowrap">{formatTime(s.startDt)}</td>
              <td className="px-3 py-2.5 text-center">
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badge.bg}`}>
                  {badge.label}
                </span>
              </td>
              <td className="px-3 py-2.5 text-gray-800 font-medium text-xs">{s.customerName || s.groomName || s.brideName || '-'}</td>
              <td className="px-3 py-2.5 text-gray-600 text-xs">{s.phone || s.contactPhone || '-'}</td>
              <td className="px-3 py-2.5 text-gray-700 text-xs">{s.assignedStaffName || s.staffName || '-'}</td>
              <td className="px-3 py-2.5 text-center">
                <input type="checkbox" checked={s.status === '완료'}
                  onChange={() => handleStatusToggle(s.weddingHallScheduleIdx || s.scheduleIdx || s.id, s.status)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 cursor-pointer" />
              </td>
              <td className="px-3 py-2.5 text-gray-500 text-xs truncate max-w-[200px]">{s.memo || '-'}</td>
            </tr>
          );
        })}
        {items.length === 0 && (
          <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">등록된 상담일정이 없습니다.</td></tr>
        )}
      </tbody>
    </table>
  );

  // ===== 월간 캘린더 셀 내용 =====
  const renderCalendarCell = (schedules: any[]) => {
    return schedules.slice(0, 4).map((s: any, idx: number) => {
      const eventType = s.eventType || '';
      const badgeInfo = EVENT_BADGE_COLORS[eventType];
      const isEvent = s.scheduleCategory === 'EVENT' || s.section === 'EVENT';
      const dotColor = badgeInfo?.dot || 'bg-gray-400';
      const badgeBg = badgeInfo?.bg || 'bg-gray-100 text-gray-600';
      return (
        <div key={idx} className="text-[10px] leading-tight mb-1 px-1 py-0.5 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer truncate">
          <div className="flex items-center gap-1">
            <span className={`w-1.5 h-1.5 rounded-full ${dotColor} shrink-0`}></span>
            <span className="text-gray-500">{formatTime(s.startDt)}</span>
            {isEvent && eventType && (
              <span className={`text-[9px] px-1 py-0 rounded font-bold ${badgeBg}`}>
                {getEventTypeLabel(eventType)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2.5">
            <span className="text-gray-800 font-medium truncate">
              {s.customerName || s.groomName || s.brideName || ''}
            </span>
            {(s.venueName || s.venue) && (
              <span className="text-gray-400">[{s.venueName || s.venue}]</span>
            )}
          </div>
          {isEvent && s.guaranteeCount && (
            <div className="ml-2.5 mt-0.5">
              <span className="inline-flex items-center px-1 py-0 rounded bg-gray-200 text-gray-600 text-[9px] font-medium">
                {s.guaranteeCount.toLocaleString()}명
              </span>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">

        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">일정관리</h1>
            <p className="text-sm text-gray-600 mt-1">일간/월간 일정을 확인하고 관리합니다</p>
          </div>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button onClick={() => setActiveTab('daily')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 'daily' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <i className="ri-calendar-todo-line mr-1.5"></i>일간 일정
          </button>
          <button onClick={() => setActiveTab('monthly')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
              activeTab === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <i className="ri-calendar-line mr-1.5"></i>월간 일정
          </button>
        </div>

        {/* ===== 일간 일정 탭 ===== */}
        {activeTab === 'daily' && (
          <div>
            {/* 날짜 선택 */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button onClick={prevDay} className="p-2 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
              </button>
              <h2 className="text-lg font-bold text-gray-800 min-w-[240px] text-center">{dailyLabel}</h2>
              <button onClick={nextDay} className="p-2 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
              </button>
              <button onClick={() => setDailyDate(new Date())}
                className="ml-2 text-xs px-3 py-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 cursor-pointer">
                오늘
              </button>
            </div>

            {/* 요약 */}
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm text-gray-500">전체 <span className="font-bold text-gray-800">{totalDailyCount}</span>건</span>
              {Object.entries(SECTION_CONFIG).map(([key, cfg]) => (
                <span key={key} className="text-xs text-gray-400 flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${cfg.color}`}></span>
                  {cfg.label} {sections[key as SectionKey].length}건
                </span>
              ))}
            </div>

            {isDailyLoading ? (
              <div className="flex justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {(Object.entries(SECTION_CONFIG) as [SectionKey, typeof SECTION_CONFIG[SectionKey]][]).map(([key, cfg]) => (
                  <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {/* 섹션 헤더 */}
                    <div
                      className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleSection(key)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-1 h-6 rounded-full ${cfg.color}`}></div>
                        <i className={`${cfg.icon} text-gray-500`}></i>
                        <span className="font-semibold text-gray-800 text-sm">{cfg.label}</span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                          {sections[key].length}건
                        </span>
                      </div>
                      <i className={`ri-arrow-${collapsedSections[key] ? 'down' : 'up'}-s-line text-gray-400 text-lg`}></i>
                    </div>
                    {/* 섹션 내용 */}
                    {!collapsedSections[key] && (
                      <div className="border-t border-gray-100">
                        {key === 'event'
                          ? renderEventTable(sections.event)
                          : renderConsultationTable(sections.consultation)
                        }
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ===== 월간 일정 탭 ===== */}
        {activeTab === 'monthly' && (
          <div>
            {/* 월 네비게이터 */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                  <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
                </button>
                <h2 className="text-sm font-bold text-gray-800">{monthYear}년 {monthMonth + 1}월</h2>
                <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded cursor-pointer">
                  <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
                </button>
              </div>

              {/* 범례 */}
              <div className="flex items-center gap-4 px-5 py-2 border-b border-gray-100 bg-gray-50/50">
                {Object.entries(EVENT_BADGE_COLORS).map(([type, info]) => (
                  <div key={type} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${info.dot}`}></span>
                    <span className="text-xs text-gray-600">{info.label}</span>
                  </div>
                ))}
              </div>

              {isMonthLoading ? (
                <div className="flex justify-center p-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div>
                  {/* 요일 헤더 */}
                  <div className="grid grid-cols-7 border-b border-gray-100">
                    {DAY_NAMES.map((day, i) => (
                      <div key={day} className={`py-2 text-center text-[11px] font-semibold ${i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'}`}>
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* 캘린더 그리드 */}
                  {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
                      {week.map((day, di) => {
                        if (day === null) return <div key={di} className="min-h-32 bg-gray-50/30 border-r border-gray-100 last:border-r-0"></div>;
                        const dateStr = `${monthYear}-${String(monthMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const daySchedules = getSchedulesForDay(day);
                        const isToday = dateStr === todayStr;
                        return (
                          <div key={di}
                            className={`min-h-32 border-r border-gray-100 last:border-r-0 p-1.5 cursor-pointer ${isToday ? 'bg-blue-50/50' : 'hover:bg-gray-50/50'}`}
                            onClick={() => { setDailyDate(new Date(dateStr + 'T00:00:00')); setActiveTab('daily'); }}
                          >
                            <div className={`text-xs mb-1 ${di === 0 ? 'text-red-400' : di === 6 ? 'text-blue-400' : 'text-gray-600'} ${isToday ? 'font-bold' : ''}`}>
                              {isToday ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-[10px]">{day}</span>
                              ) : (
                                <span>{day}</span>
                              )}
                            </div>
                            <div className="space-y-0.5 overflow-hidden">
                              {renderCalendarCell(daySchedules)}
                              {daySchedules.length > 4 && (
                                <div className="text-[9px] text-gray-400 pl-1">+{daySchedules.length - 4}건 더</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
